"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertToApiFilters = exports.convertToApiSorts = exports.convertToApiHighlight = exports.convertToTableSorts = exports.useTableData = void 0;
const common_types_1 = require("../../../common-types");
const react_1 = require("react");
const lodash_1 = require("lodash");
const evidence_defaults_context_1 = require("../../evidence-defaults/evidence-defaults-context");
const event_callback_hook_1 = require("../../../utils/event-callback-hook");
const first_render_1 = require("../../../utils/first-render");
const force_render_1 = require("../../../utils/force-render");
const update_effect_1 = require("../../../utils/update-effect");
function useTableData({ source, searchQuery, sorts, preFilters, filters, filtersState, advancedFilters, highlights, nestedFulltextFields, include, loaderRef, }) {
    const { forceRender } = (0, force_render_1.useForceRender)();
    const { sortMissingValues } = (0, react_1.useContext)(evidence_defaults_context_1.EvidenceDefaultsContext);
    const sourceParams = (0, react_1.useMemo)(() => {
        const filtersWithState = filters.map((filter, i) => ({ ...filter, ...filtersState[i] }));
        return {
            sort: convertToApiSorts(sorts, sortMissingValues),
            filters: convertToApiFilters(preFilters, filtersWithState, advancedFilters, searchQuery, nestedFulltextFields),
            highlight: convertToApiHighlight(highlights),
            include: include,
            size: 30,
        };
    }, [
        advancedFilters,
        filters,
        filtersState,
        highlights,
        include,
        nestedFulltextFields,
        preFilters,
        searchQuery,
        sortMissingValues,
        sorts,
    ]);
    const sourceParamsKey = (0, react_1.useMemo)(() => JSON.stringify(sourceParams), [sourceParams]);
    const setSourceParams = (0, event_callback_hook_1.useEventCallback)(() => {
        source.setParams(sourceParams);
    });
    (0, first_render_1.useFirstRender)(() => {
        setSourceParams();
    });
    (0, update_effect_1.useUpdateEffect)(() => {
        setSourceParams();
        source.reset();
        forceRender();
        requestAnimationFrame(() => {
            var _a;
            (_a = loaderRef.current) === null || _a === void 0 ? void 0 : _a.resetloadMoreItemsCache(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceParamsKey]);
}
exports.useTableData = useTableData;
function convertToTableSorts(sorts, columns) {
    const getDatakey = (sort) => { var _a; return (_a = columns.find((column) => column.sortkey === sort.field)) === null || _a === void 0 ? void 0 : _a.datakey; };
    return sorts.map((sort) => {
        var _a;
        return ({
            datakey: (_a = getDatakey(sort)) !== null && _a !== void 0 ? _a : sort.field,
            field: sort.field,
            type: sort.type,
            order: sort.order,
        });
    });
}
exports.convertToTableSorts = convertToTableSorts;
function convertToApiHighlight(highlights) {
    if (!highlights.length) {
        return undefined;
    }
    return {
        fields: highlights.reduce((acc, field) => {
            acc[field] = {};
            return acc;
        }, {}),
        numberOfFragments: 9999,
    };
}
exports.convertToApiHighlight = convertToApiHighlight;
function convertToApiSorts(sorts, sortMissingValues = 'LAST') {
    return sorts.map((sort) => {
        let missing = 'LAST';
        if (sortMissingValues === 'FIRST_ON_ASC' && sort.order === 'ASC') {
            missing = 'FIRST';
        }
        if (sortMissingValues === 'FIRST_ON_DESC' && sort.order === 'DESC') {
            missing = 'FIRST';
        }
        if (sortMissingValues === 'FIRST') {
            missing = 'FIRST';
        }
        return {
            field: sort.field,
            order: sort.order,
            type: sort.type,
            missing,
        };
    });
}
exports.convertToApiSorts = convertToApiSorts;
/**
 * Translares table filters to api filters.
 *
 * Combines pre-filters, table filters with search query, removes disabled or empty filters
 * @param preFilters
 * @param filters
 * @param searchQuery
 */
function convertToApiFilters(preFilters, filters, advancedFilters, searchQuery, nestedFulltextFields = []) {
    const apiFilters = filters
        .filter((filter) => filter.enabled)
        .filter(filterEmptyFilter)
        .map((filter) => {
        const { operation, filterkey, value, filters, highlight, asciiFolding, filterPath, } = filter;
        return {
            field: filterkey,
            operation: operation,
            path: filterPath,
            value,
            filters,
            highlight,
            asciiFolding,
        };
    });
    if (searchQuery !== '') {
        apiFilters.push(createFulltextFilter(searchQuery, nestedFulltextFields));
    }
    apiFilters.push(...advancedFilters);
    if (preFilters.length) {
        apiFilters.push(...preFilters.filter(({ value, operation, filters }) => (value !== undefined && value !== null) ||
            ([
                common_types_1.ApiFilterOperation.OR,
                common_types_1.ApiFilterOperation.AND,
                common_types_1.ApiFilterOperation.NOT,
            ].includes(operation) &&
                (filters !== null && filters !== void 0 ? filters : []).length > 0) ||
            (operation === common_types_1.ApiFilterOperation.IS_NULL && value === undefined)));
    }
    return deriveNestedFilters(apiFilters);
}
exports.convertToApiFilters = convertToApiFilters;
function createFulltextFilter(searchQuery, nestedFulltextFields) {
    const fulltextFilter = {
        field: '',
        operation: common_types_1.ApiFilterOperation.FTX,
        value: searchQuery,
    };
    if (!nestedFulltextFields.length) {
        return fulltextFilter;
    }
    return {
        operation: common_types_1.ApiFilterOperation.OR,
        filters: [
            fulltextFilter,
            ...nestedFulltextFields.map(({ path, fields }) => ({
                operation: common_types_1.ApiFilterOperation.NESTED,
                path,
                filter: {
                    operation: common_types_1.ApiFilterOperation.OR,
                    filters: fields.map((field) => ({
                        field,
                        operation: common_types_1.ApiFilterOperation.FTXF,
                        value: searchQuery,
                    })),
                },
            })),
        ],
    };
}
function filterEmptyFilter({ value, operation }) {
    if (value == null && operation === common_types_1.ApiFilterOperation.EQ) {
        return false;
    }
    return true;
}
/**
 * Derives nested filters.
 */
const deriveNestedFilters = (filters) => {
    const NON_NESTED = 'NON_NESTED';
    const derivedFilters = [];
    const groupedFilters = (0, lodash_1.groupBy)(filters, (filter) => {
        var _a;
        return (filter === null || filter === void 0 ? void 0 : filter.operation) === common_types_1.ApiFilterOperation.NESTED
            ? NON_NESTED
            : (_a = filter === null || filter === void 0 ? void 0 : filter.path) !== null && _a !== void 0 ? _a : NON_NESTED;
    });
    (0, lodash_1.keys)(groupedFilters).forEach((path) => {
        var _a;
        if (path === NON_NESTED) {
            derivedFilters.push(...((_a = groupedFilters === null || groupedFilters === void 0 ? void 0 : groupedFilters[NON_NESTED]) !== null && _a !== void 0 ? _a : []));
        }
        else {
            derivedFilters.push({
                operation: common_types_1.ApiFilterOperation.NESTED,
                path,
                filter: {
                    operation: common_types_1.ApiFilterOperation.AND,
                    filters: groupedFilters[path],
                },
            });
        }
    });
    return derivedFilters;
};
//# sourceMappingURL=table-data-hook.js.map